import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Gift, Check, X } from "lucide-react";

const AuthModal = ({ isOpen, onClose, initialReferralCode }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    referral_code: ""
  });
  const [referralValidation, setReferralValidation] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);

  // Set initial referral code from URL
  useEffect(() => {
    if (initialReferralCode) {
      setFormData(prev => ({ ...prev, referral_code: initialReferralCode }));
      setMode("register");
      validateReferralCode(initialReferralCode);
    }
  }, [initialReferralCode]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 6) {
      setReferralValidation(null);
      return;
    }

    setValidatingCode(true);
    try {
      const response = await axios.get(`${API}/referral/validate/${code}`);
      setReferralValidation(response.data);
    } catch (error) {
      setReferralValidation({ valid: false, message: "Invalid code" });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleReferralCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setFormData({ ...formData, referral_code: code });
    
    // Debounce validation
    if (code.length >= 6) {
      setTimeout(() => validateReferralCode(code), 500);
    } else {
      setReferralValidation(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
        toast.success("Welcome back, athlete!");
      } else {
        await register(
          formData.email, 
          formData.password, 
          formData.name,
          formData.referral_code || null
        );
        if (referralValidation?.valid) {
          toast.success(`Account created! 15% discount will apply to your first purchase!`);
        } else {
          toast.success("Account created! Time to grind!");
        }
      }
      onClose();
      setFormData({ email: "", password: "", name: "", referral_code: "" });
      setReferralValidation(null);
    } catch (error) {
      // Handle different error formats
      const errorData = error.response?.data;
      let errorMessage = "Authentication failed";
      
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation error format
          const firstError = errorData.detail[0];
          if (firstError?.msg) {
            // Extract clean message from "Value error, ..." format
            errorMessage = firstError.msg.replace('Value error, ', '');
          }
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setFormData({ email: "", password: "", name: "", referral_code: initialReferralCode || "" });
    setReferralValidation(null);
    if (mode === "login" && initialReferralCode) {
      validateReferralCode(initialReferralCode);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-void-black border border-white/10 max-w-md p-0 overflow-hidden">
        {/* Header */}
        <div className="relative p-6 border-b border-white/5">
          <div className="text-center">
            <div className="font-graffiti text-2xl mb-2">
              <span className="neon-text-blue">GRIND</span>
              <span className="neon-text-pink">TIME</span>
            </div>
            <h2 className="font-orbitron text-lg text-white uppercase">
              {mode === "login" ? "Welcome Back" : "Join The Team"}
            </h2>
            <DialogDescription className="sr-only">
              {mode === "login" ? "Login to your GRIND TIME account" : "Create a new GRIND TIME account"}
            </DialogDescription>
          </div>
        </div>

        {/* Referral Banner (only show in register mode with valid code) */}
        {mode === "register" && referralValidation?.valid && (
          <div className="mx-6 mt-4 p-3 bg-neon-green/10 border border-neon-green/30 flex items-center gap-3">
            <Gift className="w-5 h-5 text-neon-green flex-shrink-0" />
            <div>
              <p className="font-orbitron text-xs text-neon-green">REFERRAL BONUS!</p>
              <p className="font-rajdhani text-xs text-gray-400">
                {referralValidation.message}
              </p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode === "register" && (
            <>
              <div className="space-y-2">
                <Label className="font-orbitron text-xs text-gray-400 uppercase">Name</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  required
                  className="bg-void-black border-white/10 text-white font-rajdhani focus:border-neon-blue"
                  data-testid="auth-name-input"
                />
              </div>

              {/* Referral Code Input */}
              <div className="space-y-2">
                <Label className="font-orbitron text-xs text-gray-400 uppercase flex items-center gap-2">
                  <Gift className="w-3 h-3" />
                  Referral Code (Optional)
                </Label>
                <div className="relative">
                  <Input
                    type="text"
                    value={formData.referral_code}
                    onChange={handleReferralCodeChange}
                    placeholder="e.g., GRINDMIKE1234"
                    className="bg-void-black border-white/10 text-white font-rajdhani focus:border-neon-blue pr-10 uppercase"
                    data-testid="auth-referral-input"
                  />
                  {validatingCode && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
                  )}
                  {!validatingCode && referralValidation?.valid && (
                    <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neon-green" />
                  )}
                  {!validatingCode && referralValidation && !referralValidation.valid && (
                    <X className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />
                  )}
                </div>
                {referralValidation && !referralValidation.valid && (
                  <p className="text-xs text-red-400 font-rajdhani">{referralValidation.message}</p>
                )}
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400 uppercase">Email</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="you@example.com"
              required
              className="bg-void-black border-white/10 text-white font-rajdhani focus:border-neon-blue"
              data-testid="auth-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400 uppercase">Password</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              required
              minLength={6}
              className="bg-void-black border-white/10 text-white font-rajdhani focus:border-neon-blue"
              data-testid="auth-password-input"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full cyber-btn pulse-glow disabled:opacity-50 relative z-10"
            data-testid="auth-submit-btn"
          >
            <span className="flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === "login" ? "ENTER THE GRIND" : "CREATE ACCOUNT"}
            </span>
          </button>
        </form>

        {/* Footer */}
        <div className="p-6 pt-0 text-center">
          <p className="font-rajdhani text-sm text-gray-500">
            {mode === "login" ? "Don't have an account?" : "Already have an account?"}{" "}
            <button
              type="button"
              onClick={switchMode}
              className="text-neon-blue hover:text-neon-pink transition-colors"
              data-testid="auth-switch-mode"
            >
              {mode === "login" ? "Sign up" : "Login"}
            </button>
          </p>
        </div>

        {/* Admin Hint */}
        <div className="px-6 pb-6">
          <div className="bg-white/5 p-3 text-center">
            <p className="font-rajdhani text-xs text-gray-600">
              Admin: admin@grindtime.com / grindtime2024
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
